import { batch, useDispatch } from 'react-redux';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import styled from 'styled-components';
import { tokens } from '@companycam/slab';

import { graphQLIdDecoder } from 'helpers/graphql-utils';
import {
  defaultActiveFilter,
  templateView,
} from 'components/projects/taskList/constants';
import {
  setActiveFilterId,
  setCurrentTemplateId,
  setView,
} from 'actions/todos';
import useCreateTaskListFromTemplate from '../hooks/useCreateTaskFromTemplates';
import { ADMIN, MANAGER, hasRoleAccess } from 'helpers/roles';
import { useCurrentUser } from 'javascripts/hooks';

import CardMenu from 'components/projects/taskList/components/CardMenu';
import CardMenuItem from 'components/projects/taskList/components/CardMenuItem';
import TemplateBeingEditedLabel from 'components/projects/taskList/templates/TemplateBeingEditedLabel';
import OwnerInfo from './OwnerInfo';
import { Text } from '../sharedStyles';
import { TemplateModalContext } from '../TemplateModalContext';

const ItemContainer = styled.div`
  display: flex;
  justify-content: space-between;
  border: none;
  border-bottom: solid 1px ${tokens.color_border_default_le};
  width: 100%;
  align-items: center;
  background: none;
  min-height: 80px;
  padding: var(--cc_size_spacing_m) var(--cc_size_spacing_l);
  opacity: ${({ disabled }) => (disabled ? '.65' : '1')};
  cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};
  ${({ disabled }) =>
    !disabled
      ? `&:hover {
    background-color: var(--cc_color_button_background_tint);
  }`
      : null}
  transition: 0.2s ease-in-out;
`;

const ItemDetails = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex: 1;
  text-align: left;
  overflow: hidden;
  margin-right: var(--cc_size_spacing_l);
`;

const Title = styled.h2`
  color: var(--cc_color_text_default);
  font-weight: 600;
  font-size: var(--cc_size_text_m);
  margin: 0;
  white-space: nowrap;
  text-align: left;
  background: none;
  border: none;
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;
`;

const Description = styled.p`
  margin: var(--cc_size_spacing_xs) 0 0;
  color: var(--cc_color_text_subtle);
  font-size: var(--cc_size_text_s);
  white-space: nowrap;
  overflow: hidden;
  display: block;
  text-overflow: ellipsis;

  &:empty {
    display: none;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex: none;
`;

const CardMenuItemText = styled(Text)`
  color: ${(props) => props.color};
  display: flex;
  align-items: center;
  gap: var(--cc_size_spacing_s);

  i {
    font-size: var(--cc_size_text_m);
    line-height: 1;
  }
`;

const messages = defineMessages({
  previewTemplate: {
    id: 'Projects.taskList.templates.preview.v2',
    defaultMessage: 'Preview Template',
    description: 'Button label to preview template',
  },
});

const NOOP = () => {};

const TemplateListItem = ({ template }) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const { projectNodeId, onRequestClose } = useContext(TemplateModalContext);

  const user = useCurrentUser();
  const hasManagePermissions = hasRoleAccess(user, [ADMIN, MANAGER]);
  const isTemplateOwnerEditing = template.beingEdited && !template.isOwner;
  const { databaseId: projectId } = graphQLIdDecoder(projectNodeId);

  const { createTaskListFromTemplate } = useCreateTaskListFromTemplate({
    template,
    projectNodeId,
    onSuccess: (createdTaskListId) => {
      dispatch(setActiveFilterId(defaultActiveFilter));
      onRequestClose();
      if (createdTaskListId) {
        window.Turbo.visit(`/projects/${projectId}/todos/${createdTaskListId}`);
      }
    },
  });

  return (
    <ItemContainer
        disabled={isTemplateOwnerEditing}
        onClick={isTemplateOwnerEditing ? NOOP : createTaskListFromTemplate}
      >
        <ItemDetails>
          {isTemplateOwnerEditing ? <TemplateBeingEditedLabel /> : null}
          <Title numberOfLines={1}>{template.name}</Title>
          <Description>{template.description}</Description>
          {!template.isOwner && template.owner ? (
            <OwnerInfo owner={template.owner} />
          ) : null}
        </ItemDetails>
        <ButtonContainer>
          <CardMenu disabled={isTemplateOwnerEditing}>
            <CardMenuItem
              onClick={(e) => {
                e.stopPropagation();
                batch(() => {
                  dispatch(setCurrentTemplateId(template.id));
                  dispatch(setView({ view: templateView.PREVIEW }));
                });
              }}
            >
              <CardMenuItemText color={tokens.color_link_secondary_le}>
                <i className="mdi mdi-eye-outline" />
                {formatMessage(messages.previewTemplate)}
              </CardMenuItemText>
            </CardMenuItem>
            {hasManagePermissions && template.isOwner ? (
              <>
                <CardMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(setCurrentTemplateId(template.id));
                    dispatch(setView({ view: templateView.EDIT }));
                  }}
                >
                  <CardMenuItemText color={tokens.color_link_secondary_le}>
                    <i className="mdi mdi-pencil-outline" />
                    {formatMessage({ id: 'Projects.taskList.templates.edit' })}
                  </CardMenuItemText>
                </CardMenuItem>
                <CardMenuItem
                  className="dropdown-menu__link"
                  onClick={(e) => {
                    e.stopPropagation();
                    dispatch(setCurrentTemplateId(template.id));
                    dispatch(
                      setView({ view: templateView.DELETE_TEMPLATE_CONFIRM }),
                    );
                  }}
                >
                  <CardMenuItemText>
                    <i className="mdi mdi-delete-outline" />
                    <FormattedMessage
                      id="Projects.taskList.templates.delete"
                      defaultMessage="Delete Template"
                      description="Text for deleting template"
                    />
                  </CardMenuItemText>
                </CardMenuItem>
              </>
            ) : null}
          </CardMenu>
        </ButtonContainer>
      </ItemContainer>
  );
};

TemplateListItem.propTypes = {
  template: PropTypes.object,
};

export default TemplateListItem;